import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import DOMPurify from 'dompurify';
import classnames from 'classnames';
import styles from './ProductTitleInfo.module.scss';
import Ratings from '../../../components/common/Ratings';
import useDrupalSettings from '../../../hooks/useDrupalSettings';
import messages from './messages';
import AffirmAsLowAs from '../../../components/AffirmAsLowAs';
import drupalPubSubService from '../../../services/drupalPubSub/drupalPubSub';
import { priceToString } from '../../../utils';
import {
  extractTextFromHtml,
  UNDERSCORE,
  UPPERCASE_KING,
  UPPERCASE_XL,
} from './constants';
import {
  BundleDrupalSettings,
  BundleProduct,
} from '../AddToCartBundlePdpContainer/types';
import { BundlePdpInStore } from '../../../slices/bundlePdp/types';

const ProductTitleInfo = () => {
  const { formatMessage, formatNumber } = useIntl();
  const { publish } = drupalPubSubService;
  const {
    bundle,
    product,
    global_badge: globalBadge,
  } = useDrupalSettings() as BundleDrupalSettings;
  const { priceTotal, discountPriceTotal, selectedAttributesValues } =
    useSelector((state: { bundlePdp: BundlePdpInStore }) => state.bundlePdp);
  const [selectedProduct, setSelectedProduct] = useState<BundleProduct>();
  const [selectedSize, setSelectedSize] = useState<string>();
  const { sanitize } = DOMPurify;

  useEffect(() => {
    setSelectedProduct(bundle?.[0]);
    if (selectedAttributesValues?.size) {
      const { size } = selectedAttributesValues;
      const formattedSize = `${size[0].toUpperCase()}${size.slice(1)}`;
      const SIZE_MAP = {
        xl: UPPERCASE_XL,
        king: UPPERCASE_KING,
      };
      let newSize = formattedSize.replace(UNDERSCORE, ' ');

      Object.entries(SIZE_MAP).forEach(([lower, upper]) => {
        newSize = newSize.replace(lower, upper);
      });

      setSelectedSize(newSize);
    }
  }, [bundle, selectedAttributesValues]);

  const handleBadgeIconClick = useCallback(() => {
    if (globalBadge) {
      publish({
        subscription: 'onShowModal',
        parameters: {
          micromodalTrigger: globalBadge.modal_id,
        },
      });
    }
  }, [globalBadge, publish]);

  const renderBadges = useMemo(() => {
    const promoTextFormatted = extractTextFromHtml(
      product?.promotion_text ?? '',
    );
    return promoTextFormatted ? (
      <div className={styles['product-badge-wrapper']}>
        {globalBadge && (
          <div
            className={classnames(
              styles['product-global-badge'],
              globalBadge.classes,
            )}
          >
            {globalBadge.name}
            {globalBadge.icon && (
              <button
                type='button'
                onClick={handleBadgeIconClick}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleBadgeIconClick();
                  }
                }}
              >
                <img
                  src={globalBadge.icon.url as unknown as string}
                  alt={globalBadge.icon.url as unknown as string}
                />
              </button>
            )}
          </div>
        )}
        <div className={styles['product-badge']}>{promoTextFormatted}</div>
      </div>
    ) : null;
  }, [globalBadge, handleBadgeIconClick, product?.promotion_text]);

  return (
    <>
      <div className={styles['product-title-section']}>
        {selectedProduct?.ratingInfo && (
          <Ratings
            rating={selectedProduct?.ratingInfo?.rating}
            label={formatMessage(messages.ratingLabel)}
            reviews={selectedProduct?.ratingInfo?.reviewCount}
            reviewLink='#section-id-purple-reviews'
          />
        )}
        {renderBadges}
        <h2 className={styles['product-title']}>{product?.title}</h2>
      </div>
      <div className={styles['product-pricing-section']}>
        <div className={styles['product-pricing-text-group']}>
          {product?.use_size_matching_logic && selectedSize && (
            <span className={styles['product-size']}>{selectedSize}</span>
          )}
          <span className={styles['pricing-group']}>
            <span className={styles['strikethrough-price']}>
              {priceToString({
                price: {
                  centAmount:
                    (priceTotal?.centAmount ?? 0) +
                    (discountPriceTotal?.centAmount ?? 0),
                  fractionDigits: 2,
                  currencyCode: 'USD',
                },
                formatNumber,
                formatMessage,
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
              })}
            </span>
            <span className={styles['discounted-price']}>
              {priceToString({
                price: {
                  centAmount: priceTotal?.centAmount,
                  fractionDigits: 2,
                  currencyCode: 'USD',
                },
                formatNumber,
                formatMessage,
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
              })}
            </span>
          </span>
        </div>
        <div className={styles['pricing-finance']}>
          <span className={styles['pricing-finance-amount']}>
            <AffirmAsLowAs
              total={priceTotal?.centAmount}
            />
          </span>
        </div>

        {product?.promotion_text?.length && (
          <div
            className={styles['product-promotion']}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitize(product?.promotion_text),
            }}
          />
        )}
        {product?.promotion_disclaimer?.length && (
          <div className={styles.promotionDisclaimer}>
            *{product?.promotion_disclaimer}
          </div>
        )}
      </div>
    </>
  );
};

export default ProductTitleInfo;
